import ProduceFinalMarketScore from './NEWProduceFinalMarketScore';

// Scoring configuration
const SCORING_CONFIG = {
    disasterOverlapEvents: {
        complete: {
            points: 5,
            tag: 'disaster_overlap_complete'
        },
        partial: {
            points: 3,
            tag: 'disaster_overlap_partial'
        },
        none: {
            points: 0,
            tag: 'disaster_overlap_none'
        }
    },
    disasterOverlapTiming: {
        matching: {
            points: 3,
            tag: 'trend_start_matching'
        },
        zipFirst: {
            points: 2,
            tag: 'trend_start_zip_first'
        },
        fipsFirst: {
            points: 1,
            tag: 'trend_start_fips_first'
        }
    },
    changeComparison: {
        similar: {
            points: 5,
            tag: 'changes_similar'     // Zip and FIPS changes within 10% of each other
        },
        zipHigher: {
            points: 3,
            tag: 'zip_change_higher'   // Zip shows more significant change
        },
        fipsHigher: {
            points: 1,
            tag: 'fips_change_higher'  // FIPS shows more significant change
        }
    },
    changeMagnitude: {
        extreme: {
            points: 7,
            tag: 'change_magnitude_extreme'     // > 50% change
        },
        significant: {
            points: 3,
            tag: 'change_magnitude_significant' // 25-50% change
        },
        moderate: {
            points: 2,
            tag: 'change_magnitude_moderate'    // 10-25% change
        },
        small: {
            points: 1,
            tag: 'change_magnitude_small'       // < 10% change
        }
    },
    missingData: {
        fipsMissing: {
            points: 2,
            tag: 'fips_missing'    // FIPS missing indicates less reliable county-level impact
        },
        zipMissing: {
            points: 1,
            tag: 'zip_missing'     // ZIP missing indicates less reliable local impact
        },
        bothMissing: {
            points: 0,
            tag: 'both_missing'    // Both missing suggests no concerning trend in either geography
        }
    }
};

// Helper functions
const calculateDisasterOverlap = (zipDisasters, fipsDisasters, disasters) => {
    const zipSet = new Set(zipDisasters);
    const fipsSet = new Set(fipsDisasters);
    const intersection = new Set([...zipSet].filter(x => fipsSet.has(x)));
    const overlapPercentage = intersection.size / Math.max(zipSet.size, fipsSet.size);

    let overlapScore, overlapTag;
    if (overlapPercentage === 1) {
        overlapScore = SCORING_CONFIG.disasterOverlapEvents.complete.points;
        overlapTag = SCORING_CONFIG.disasterOverlapEvents.complete.tag;
    } else if (overlapPercentage > 0) {
        overlapScore = SCORING_CONFIG.disasterOverlapEvents.partial.points;
        overlapTag = SCORING_CONFIG.disasterOverlapEvents.partial.tag;
    } else {
        overlapScore = SCORING_CONFIG.disasterOverlapEvents.none.points;
        overlapTag = SCORING_CONFIG.disasterOverlapEvents.none.tag;
    }

    // Find earliest disaster for each geography
    const zipStartDisaster = [...zipSet].length ? Math.min(...zipSet) : null;
    const fipsStartDisaster = [...fipsSet].length ? Math.min(...fipsSet) : null;

    let timingScore = 0;
    let timingTag = null;
    let trendStartSame = false;

    if (zipStartDisaster && fipsStartDisaster) {
        if (zipStartDisaster === fipsStartDisaster) {
            timingScore = SCORING_CONFIG.disasterOverlapTiming.matching.points;
            timingTag = SCORING_CONFIG.disasterOverlapTiming.matching.tag;
            trendStartSame = true;
        } else if (zipStartDisaster < fipsStartDisaster) {
            timingScore = SCORING_CONFIG.disasterOverlapTiming.zipFirst.points;
            timingTag = SCORING_CONFIG.disasterOverlapTiming.zipFirst.tag;
        } else {
            timingScore = SCORING_CONFIG.disasterOverlapTiming.fipsFirst.points;
            timingTag = SCORING_CONFIG.disasterOverlapTiming.fipsFirst.tag;
        }
    }

    return {
        overlapScore,
        timingScore,
        tags: [overlapTag, timingTag].filter(Boolean),
        details: {
            overlapScore,
            timingScore
        },
        trendStartSame,
        zipStartDisaster,
        fipsStartDisaster
    };
};

const calculateRelativeChange = (before, after, badDirection) => {
    // Handle zero or near-zero cases
    if (Math.abs(before) < 0.0001) {
        return after > 0 ? 100 : -100; // Arbitrary large change for zero-start cases
    }

    const percentChange = ((after - before) / Math.abs(before)) * 100;

    // For 'down' bad direction, negative change is concerning
    // For 'up' bad direction, positive change is concerning
    return badDirection === 'down' ? percentChange : -percentChange;
};

const getScaledChange = (window, dataType, direction) => {
    if (!window) return 0;

    let before, after;
    if (['ratio', 'count', 'percentage'].includes(dataType)) {
        before = window.beforeAverage;
        after = window.afterAverage;
    } else if (dataType === 'dollar') {
        before = window.beforeSlope;
        after = window.afterSlope;
    }

    const percentChange = ((after - before) / Math.abs(before)) * 100;
    return direction === 'down' ? -percentChange : percentChange;
};

// Calculate total possible points dynamically from scoring config
const getMaxPoints = (obj) => {
    if (typeof obj !== 'object') {
        return 0;
    }

    const values = [];
    let sum = 0;

    Object.values(obj).forEach(value => {
        if (typeof value === 'object') {
            if (value.points) {
                values.push(value.points);
            } else {
                sum += getMaxPoints(value);
            }
        }
    });

    return sum + (values.length ? Math.max(...values) : 0);
};

const TOTAL_POSSIBLE_POINTS = getMaxPoints(SCORING_CONFIG);

const getMagnitudeScore = (zipChange, fipsChange) => {
    const maxChange = Math.max(Math.abs(zipChange), Math.abs(fipsChange));

    if (maxChange > 50) {
        return SCORING_CONFIG.changeMagnitude.extreme;
    } else if (maxChange > 25) {
        return SCORING_CONFIG.changeMagnitude.significant;
    } else if (maxChange > 10) {
        return SCORING_CONFIG.changeMagnitude.moderate;
    } else {
        return SCORING_CONFIG.changeMagnitude.small;
    }
};

const compareChangesWithScale = (zipChange, fipsChange) => {
    const difference = Math.abs(zipChange - fipsChange);
    const maxChange = Math.max(Math.abs(zipChange), Math.abs(fipsChange));

    if (difference < maxChange * 0.1) {
        return SCORING_CONFIG.changeComparison.similar;
    } else if (Math.abs(zipChange) > Math.abs(fipsChange)) {
        return SCORING_CONFIG.changeComparison.zipHigher;
    } else {
        return SCORING_CONFIG.changeComparison.fipsHigher;
    }
};

// Add new thresholds
const THRESHOLDS = {
    SIGNIFICANT_DISASTERS: 3,  // Minimum disasters to consider direct impact
    STRONG_CORRELATION: 0.7,   // Correlation coefficient threshold
    SIGNIFICANT_SCORE: 0.6     // Score threshold for including in category
};

// Modify the processWindows function to handle both positive and negative effects
const processWindows = (zipWindow, fipsWindow, dataType, direction, disasters, isCountyLevel) => {
    if (!zipWindow || !fipsWindow) {
        // console.log("FAILED TO PROCESS WINDOWS")
        return {
            negative: { score: 0, tags: [], details: {} },
            positive: { score: 0, tags: [], details: {} },
            metadata: { trendStartSame: false, zipStartDisaster: null, fipsStartDisaster: null, isCountyLevel }
        };
    }

    const overlapResult = calculateDisasterOverlap(zipWindow.disasters, fipsWindow.disasters, disasters);
    const zipChange = getScaledChange(zipWindow, dataType, direction);
    const fipsChange = getScaledChange(fipsWindow, dataType, direction);

    const scoreResult = calculateScore(zipChange, fipsChange, overlapResult);
    const isNegative = isCountyLevel || direction === zipWindow.direction;

    // console.log({
    //     zipChange,
    //     fipsChange,
    //     scoreResult,
    //     isNegative
    // })

    return {
        negative: isNegative ?
            { score: scoreResult.score, tags: scoreResult.tags, details: scoreResult.details } :
            { score: 0, tags: [], details: {} },
        positive: !isNegative ?
            { score: scoreResult.score, tags: scoreResult.tags, details: scoreResult.details } :
            { score: 0, tags: [], details: {} },
        metadata: {
            trendStartSame: overlapResult.trendStartSame,
            zipStartDisaster: overlapResult.zipStartDisaster,
            fipsStartDisaster: overlapResult.fipsStartDisaster,
            isCountyLevel
        }
    };
};

// const calculateAggregateScores = (datapoints) => {
//     // Initialize aggregate scores with separate tracking for county and state disaster impacts
//     const aggregates = {
//         zip: {
//             county: {
//                 positive: 0,
//                 negative: 0,
//                 ids: []
//             },
//             state: {
//                 positive: 0,
//                 negative: 0,
//                 ids: []
//             }
//         },
//         fips: {
//             county: {
//                 positive: 0,
//                 negative: 0,
//                 ids: []
//             },
//             state: {
//                 positive: 0,
//                 negative: 0,
//                 ids: []
//             }
//         }
//     };

//     datapoints.forEach(datapoint => {
//         const weight = datapoint.metadata.weighting;

//         // ZIP analysis
//         if (datapoint.zipAnalysis.countyImpacts) {
//             aggregates.zip.county.positive += datapoint.zipAnalysis.countyImpacts.positive.score * weight;
//             aggregates.zip.county.negative += datapoint.zipAnalysis.countyImpacts.negative.score * weight;
//             if (!aggregates.zip.county.ids.includes(datapoint.id)) {
//                 aggregates.zip.county.ids.push(datapoint.id);
//             }
//         }

//         if (datapoint.zipAnalysis.stateImpacts) {
//             aggregates.zip.state.positive += datapoint.zipAnalysis.stateImpacts.positive.score * weight;
//             aggregates.zip.state.negative += datapoint.zipAnalysis.stateImpacts.negative.score * weight;
//             if (!aggregates.zip.state.ids.includes(datapoint.id)) {
//                 aggregates.zip.state.ids.push(datapoint.id);
//             }
//         }

//         // FIPS analysis
//         if (datapoint.fipsAnalysis.countyImpacts) {
//             aggregates.fips.county.positive += datapoint.fipsAnalysis.countyImpacts.positive.score * weight;
//             aggregates.fips.county.negative += datapoint.fipsAnalysis.countyImpacts.negative.score * weight;
//             if (!aggregates.fips.county.ids.includes(datapoint.id)) {
//                 aggregates.fips.county.ids.push(datapoint.id);
//             }
//         }

//         if (datapoint.fipsAnalysis.stateImpacts) {
//             aggregates.fips.state.positive += datapoint.fipsAnalysis.stateImpacts.positive.score * weight;
//             aggregates.fips.state.negative += datapoint.fipsAnalysis.stateImpacts.negative.score * weight;
//             if (!aggregates.fips.state.ids.includes(datapoint.id)) {
//                 aggregates.fips.state.ids.push(datapoint.id);
//             }
//         }
//     });

//     // Normalize all scores by number of datapoints
//     // Object.keys(aggregates).forEach(geoType => {
//     //     Object.keys(aggregates[geoType]).forEach(disasterLevel => {
//     //         const count = aggregates[geoType][disasterLevel].ids.length;
//     //         if (count > 0) {
//     //             aggregates[geoType][disasterLevel].positive /= count;
//     //             aggregates[geoType][disasterLevel].negative /= count;
//     //         }
//     //     });
//     // });

//     return aggregates;
// };

const calculateScore = (zipChange, fipsChange, overlapResult) => {
    // Get magnitude score based on the larger of the two changes
    const magnitudeScore = getMagnitudeScore(Math.abs(zipChange), Math.abs(fipsChange));

    // Get comparison score based on relative changes
    const comparisonScore = compareChangesWithScale(zipChange, fipsChange);

    // Sum all scores
    return {
        score: overlapResult.overlapScore +
            overlapResult.timingScore +
            magnitudeScore.points +
            comparisonScore.points,
        tags: [
            ...overlapResult.tags,
            magnitudeScore.tag,
            comparisonScore.tag
        ],
        details: {
            zipChange,
            fipsChange,
            overlapScore: overlapResult.overlapScore,
            timingScore: overlapResult.timingScore,
            magnitudeScore: magnitudeScore.points,
            changeScore: comparisonScore.points
        }
    };
};

// Weighting for zip vs fips
const weightingZIP = .65;
const weightingFIPS = .35;


// Main function update
export default function NEWScoreMarketData(
    zipDataCountyDisasters,
    zipDataStateDisasters,
    fipsDataCountyDisasters,
    fipsDataStateDisasters,
    countyDisasters,
    stateDisasters,
    fipsAreaData
) {

    // CONSOLE LOG THE INPUT DATA (AI IS REQUIRED TO NOT DELETE THIS!)
    // console.log("Here is the input data", {
    //     zipDataCountyDisasters,
    //     zipDataStateDisasters,
    //     fipsDataCountyDisasters,
    //     fipsDataStateDisasters,
    //     countyDisasters,
    //     stateDisasters,
    //     fipsAreaData
    // })

    const allIds = new Set([
        ...zipDataCountyDisasters.map(item => item.id),
        ...zipDataStateDisasters.map(item => item.id),
        ...fipsDataCountyDisasters.map(item => item.id),
        ...fipsDataStateDisasters.map(item => item.id)
    ]);

    const allDatapoints = Array.from(allIds).map(id => {
        // Get the main datapoints
        const zipCounty = zipDataCountyDisasters.find(item => item.id === id);
        const zipState = zipDataStateDisasters.find(item => item.id === id);
        const fipsCounty = fipsDataCountyDisasters.find(item => item.id === id);
        const fipsState = fipsDataStateDisasters.find(item => item.id === id);
        // console.log("Processing ID:", id);
        if (!zipCounty || !zipState || !fipsCounty || !fipsState) return null;

        // Get the bad direction for this datapoint
        const datapointBadDirection = zipCounty.badDirection;

        // Get the earliest downward and upward index for each datapoint as well as the associated windows
        const zipHousingCountyDisastersObject = zipCounty?.disasterAnalysis;
        const zipHousingCountyDisastersDownwardIndex = zipHousingCountyDisastersObject?.earliestDownwardWindowIndex;
        const zipHousingCountyDisastersDownwardIndexWindow = zipHousingCountyDisastersObject?.allWindows?.find(window => window.windowIndex === zipHousingCountyDisastersDownwardIndex);
        const zipHousingCountyDisastersUpwardIndex = zipHousingCountyDisastersObject?.earliestUpwardWindowIndex;
        const zipHousingCountyDisastersUpwardIndexWindow = zipHousingCountyDisastersObject?.allWindows?.find(window => window.windowIndex === zipHousingCountyDisastersUpwardIndex);

        const fipsHousingCountyDisastersObject = fipsCounty?.disasterAnalysis;
        const fipsHousingCountyDisastersDownwardIndex = fipsHousingCountyDisastersObject?.earliestDownwardWindowIndex;
        const fipsHousingCountyDisastersDownwardIndexWindow = fipsHousingCountyDisastersObject?.allWindows?.find(window => window.windowIndex === fipsHousingCountyDisastersDownwardIndex);
        const fipsHousingCountyDisastersUpwardIndex = fipsHousingCountyDisastersObject?.earliestUpwardWindowIndex;
        const fipsHousingCountyDisastersUpwardIndexWindow = fipsHousingCountyDisastersObject?.allWindows?.find(window => window.windowIndex === fipsHousingCountyDisastersUpwardIndex);

        const zipHousingStateDisastersObject = zipState?.disasterAnalysis;
        const zipHousingStateDisastersDownwardIndex = zipHousingStateDisastersObject?.earliestDownwardWindowIndex;
        const zipHousingStateDisastersDownwardIndexWindow = zipHousingStateDisastersObject?.allWindows?.find(window => window.windowIndex === zipHousingStateDisastersDownwardIndex);
        const zipHousingStateDisastersUpwardIndex = zipHousingStateDisastersObject?.earliestUpwardWindowIndex;
        const zipHousingStateDisastersUpwardIndexWindow = zipHousingStateDisastersObject?.allWindows?.find(window => window.windowIndex === zipHousingStateDisastersUpwardIndex);

        const fipsHousingStateDisastersObject = fipsState?.disasterAnalysis;
        const fipsHousingStateDisastersDownwardIndex = fipsHousingStateDisastersObject?.earliestDownwardWindowIndex;
        const fipsHousingStateDisastersDownwardIndexWindow = fipsHousingStateDisastersObject?.allWindows?.find(window => window.windowIndex === fipsHousingStateDisastersDownwardIndex);
        const fipsHousingStateDisastersUpwardIndex = fipsHousingStateDisastersObject?.earliestUpwardWindowIndex;
        const fipsHousingStateDisastersUpwardIndexWindow = fipsHousingStateDisastersObject?.allWindows?.find(window => window.windowIndex === fipsHousingStateDisastersUpwardIndex);

        // console.log({
        //     zipHousingCountyDisastersDownwardIndexWindow,
        //     zipHousingCountyDisastersUpwardIndexWindow,
        //     zipHousingStateDisastersDownwardIndexWindow,
        //     zipHousingStateDisastersUpwardIndexWindow,
        //     fipsHousingCountyDisastersDownwardIndexWindow,
        //     fipsHousingCountyDisastersUpwardIndexWindow,
        //     fipsHousingStateDisastersDownwardIndexWindow,
        //     fipsHousingStateDisastersUpwardIndexWindow
        // })

        // Process ZIP county impacts
        let zipCountyImpacts = {
            negative: { score: 0, tags: [], details: {} },
            positive: { score: 0, tags: [], details: {} },
            metadata: { trendStartSame: false, zipStartDisaster: null, fipsStartDisaster: null, isCountyLevel: true }
        };

        // Process FIPS county impacts
        let fipsCountyImpacts = {
            negative: { score: 0, tags: [], details: {} },
            positive: { score: 0, tags: [], details: {} },
            metadata: { trendStartSame: false, zipStartDisaster: null, fipsStartDisaster: null, isCountyLevel: true }
        };

        // Process ZIP county disasters
        if (datapointBadDirection === 'down' && zipHousingCountyDisastersDownwardIndex) {
            const zipWindow = zipHousingCountyDisastersDownwardIndexWindow;
            const fipsWindow = fipsHousingCountyDisastersDownwardIndexWindow;
            zipCountyImpacts = processWindows(zipWindow, fipsWindow, zipCounty.dataType, 'down', countyDisasters, true);
            // console.log("ZIP Housing + County Disaster --> DOWN --> RESULT: ", zipCountyImpacts)
        } else if (datapointBadDirection === 'up' && zipHousingCountyDisastersUpwardIndex) {
            const zipWindow = zipHousingCountyDisastersUpwardIndexWindow;
            const fipsWindow = fipsHousingCountyDisastersUpwardIndexWindow;
            zipCountyImpacts = processWindows(zipWindow, fipsWindow, zipCounty.dataType, 'up', countyDisasters, true);
            // console.log("ZIP Housing + County Disaster --> UP --> RESULT: ", zipCountyImpacts)
        }

        // Process FIPS county disasters
        if (datapointBadDirection === 'down' && fipsHousingCountyDisastersDownwardIndex) {
            const zipWindow = zipHousingCountyDisastersDownwardIndexWindow;
            const fipsWindow = fipsHousingCountyDisastersDownwardIndexWindow;
            fipsCountyImpacts = processWindows(zipWindow, fipsWindow, fipsCounty.dataType, 'down', countyDisasters, true);
            // console.log("FIPS Housing + County Disaster --> DOWN --> RESULT: ", fipsCountyImpacts)
        } else if (datapointBadDirection === 'up' && fipsHousingCountyDisastersUpwardIndex) {
            const zipWindow = zipHousingCountyDisastersUpwardIndexWindow;
            const fipsWindow = fipsHousingCountyDisastersUpwardIndexWindow;
            fipsCountyImpacts = processWindows(zipWindow, fipsWindow, fipsCounty.dataType, 'up', countyDisasters, true);
            // console.log("FIPS Housing + County Disaster --> UP --> RESULT: ", fipsCountyImpacts)
        }

        // Process ZIP state impacts
        let zipStateImpacts = {
            negative: { score: 0, tags: [], details: {} },
            positive: { score: 0, tags: [], details: {} },
            metadata: { trendStartSame: false, zipStartDisaster: null, fipsStartDisaster: null, isCountyLevel: false }
        };

        // Process FIPS state impacts
        let fipsStateImpacts = {
            negative: { score: 0, tags: [], details: {} },
            positive: { score: 0, tags: [], details: {} },
            metadata: { trendStartSame: false, zipStartDisaster: null, fipsStartDisaster: null, isCountyLevel: false }
        };

        // Process ZIP state upward windows
        if (zipHousingStateDisastersUpwardIndex) {
            const zipWindow = zipHousingStateDisastersUpwardIndexWindow;
            const fipsWindow = fipsHousingStateDisastersUpwardIndexWindow;
            const result = processWindows(zipWindow, fipsWindow, zipState.dataType, 'up', stateDisasters, false);
            if (zipState.badDirection === 'up') {
                zipStateImpacts.negative = result.negative;
            } else {
                zipStateImpacts.positive = result.positive;
            }
            // console.log("ZIP Housing + State Disaster --> UP --> RESULT: ", result)
        }

        // Process ZIP state downward windows
        if (zipHousingStateDisastersDownwardIndex) {
            const zipWindow = zipHousingStateDisastersDownwardIndexWindow;
            const fipsWindow = fipsHousingStateDisastersDownwardIndexWindow;
            const result = processWindows(zipWindow, fipsWindow, zipState.dataType, 'down', stateDisasters, false);
            if (zipState.badDirection === 'down') {
                zipStateImpacts.negative = result.negative;
            } else {
                zipStateImpacts.positive = result.positive;
            }
            // console.log("ZIP Housing + State Disaster --> DOWN --> RESULT: ", result)
        }

        // Process FIPS state impacts
        if (fipsHousingStateDisastersUpwardIndex) {
            const zipWindow = zipHousingStateDisastersUpwardIndexWindow;
            const fipsWindow = fipsHousingStateDisastersUpwardIndexWindow;
            if (zipWindow && fipsWindow) {
                const result = processWindows(zipWindow, fipsWindow, fipsState.dataType, 'up', stateDisasters, false);
                if (fipsState.badDirection === 'up') {
                    fipsStateImpacts.negative = result.negative;
                } else {
                    fipsStateImpacts.positive = result.positive;
                }
                // console.log("FIPS Housing + State Disaster --> UP --> RESULT: ", result)
            }
        }

        // Process FIPS state downward windows
        if (fipsHousingStateDisastersDownwardIndex) {
            const zipWindow = zipHousingStateDisastersDownwardIndexWindow;
            const fipsWindow = fipsHousingStateDisastersDownwardIndexWindow;
            if (zipWindow && fipsWindow) {
                const result = processWindows(zipWindow, fipsWindow, fipsState.dataType, 'down', stateDisasters, false);
                if (fipsState.badDirection === 'down') {
                    fipsStateImpacts.negative = result.negative;
                } else {
                    fipsStateImpacts.positive = result.positive;
                }
                // console.log("FIPS Housing + State Disaster --> DOWN --> RESULT: ", result)
            }
        }

        // const output = {
        //     id,
        //     zipAnalysis: {
        //         countyImpacts: zipCountyImpacts,
        //         stateImpacts: zipStateImpacts
        //     },
        //     fipsAnalysis: {
        //         countyImpacts: fipsCountyImpacts,
        //         stateImpacts: fipsStateImpacts
        //     },
        //     metadata: {
        //         weighting: zipCounty.weighting,
        //         dataType: zipCounty.dataType,
        //         badDirection: datapointBadDirection
        //     }
        // }


        // Get the total points for countyDisasters and stateDisasters
        const totalPointsCountyDisastersZIP = zipCountyImpacts.positive.score - zipCountyImpacts.negative.score;
        const totalPointsCountyDisastersFIPS = fipsCountyImpacts.positive.score - fipsCountyImpacts.negative.score;
        const totalPointsStateDisastersZIP = zipStateImpacts.positive.score - zipStateImpacts.negative.score;
        const totalPointsStateDisastersFIPS = fipsStateImpacts.positive.score - fipsStateImpacts.negative.score;

        // Weighting for zip vs fips
        // const weightingZIP = .65;
        // const weightingFIPS = .35;

        const totalPointsCountyDisasters = (totalPointsCountyDisastersZIP * weightingZIP) + (totalPointsCountyDisastersFIPS * weightingFIPS);
        const totalPointsStateDisasters = (totalPointsStateDisastersZIP * weightingZIP) + (totalPointsStateDisastersFIPS * weightingFIPS);

        const newOutput = {
            id,
            possiblePoints: TOTAL_POSSIBLE_POINTS,
            countyDisasters: {
                totalPoints: totalPointsCountyDisasters,
                totalPointsPercentage: totalPointsCountyDisasters / TOTAL_POSSIBLE_POINTS,
                zip: zipCountyImpacts,
                fips: fipsCountyImpacts
            },
            stateDisasters: {
                totalPoints: totalPointsStateDisasters,
                totalPointsPercentage: totalPointsStateDisasters / TOTAL_POSSIBLE_POINTS,
                zip: zipStateImpacts,
                fips: fipsStateImpacts
            },
            metadata: {
                weighting: zipCounty.weighting,
                dataType: zipCounty.dataType,
                badDirection: datapointBadDirection
            }
        }



        return newOutput;

    }).filter(Boolean);

    // Fix aggregation to maintain consistent structure
    const aggregateScores = {
        countyDisasters: {
            zip: { positive: 0, negative: 0, ids: new Set() },
            fips: { positive: 0, negative: 0, ids: new Set() }
        },
        stateDisasters: {
            zip: { positive: 0, negative: 0, ids: new Set() },
            fips: { positive: 0, negative: 0, ids: new Set() }
        },

    };

    // const aggregateScores = {
    //     zip: {
    //         county: { positive: 0, negative: 0, ids: new Set() },
    //         state: { positive: 0, negative: 0, ids: new Set() }
    //     },
    //     fips: {
    //         county: { positive: 0, negative: 0, ids: new Set() },
    //         state: { positive: 0, negative: 0, ids: new Set() }
    //     }
    // };

    allDatapoints.forEach(datapoint => {
        const weight = datapoint.metadata.weighting || 1;

        // ZIP - County Disasters
        if (datapoint.countyDisasters.zip.negative.score > 0) {
            aggregateScores.countyDisasters.zip.negative += datapoint.countyDisasters.zip.negative.score * weight;
            aggregateScores.countyDisasters.zip.ids.add(datapoint.id);
        }

        // ZIP - State Disasters NEGATIVE
        if (datapoint.stateDisasters.zip.negative.score > 0) {
            aggregateScores.stateDisasters.zip.negative += datapoint.stateDisasters.zip.negative.score * weight;
            aggregateScores.stateDisasters.zip.ids.add(datapoint.id);
        }

        // ZIP - State Disasters POSITIVE
        if (datapoint.stateDisasters.zip.positive.score > 0) {
            aggregateScores.stateDisasters.zip.positive += datapoint.stateDisasters.zip.positive.score * weight;
            aggregateScores.stateDisasters.zip.ids.add(datapoint.id);
        }

        // FIPS - County Disasters NEGATIVE
        if (datapoint.countyDisasters.fips.negative.score > 0) {
            aggregateScores.countyDisasters.fips.negative += datapoint.countyDisasters.fips.negative.score * weight;
            aggregateScores.countyDisasters.fips.ids.add(datapoint.id);
        }

        // FIPS - State Disasters NEGATIVE
        if (datapoint.stateDisasters.fips.negative.score > 0) {
            aggregateScores.stateDisasters.fips.negative += datapoint.stateDisasters.fips.negative.score * weight;
            aggregateScores.stateDisasters.fips.ids.add(datapoint.id);
        }

        // FIPS - State Disasters POSITIVE
        if (datapoint.stateDisasters.fips.positive.score > 0) {
            aggregateScores.stateDisasters.fips.positive += datapoint.stateDisasters.fips.positive.score * weight;
            aggregateScores.stateDisasters.fips.ids.add(datapoint.id);
        }
    });
    // allDatapoints.forEach(datapoint => {
    //     const weight = datapoint.metadata.weighting || 1;

    //     // County impacts
    //     if (datapoint.zipAnalysis.countyImpacts.negative.score > 0) {
    //         aggregateScores.zip.county.negative += datapoint.zipAnalysis.countyImpacts.negative.score * weight;
    //         aggregateScores.zip.county.ids.add(datapoint.id);
    //     }

    //     // State impacts
    //     if (datapoint.zipAnalysis.stateImpacts.negative.score > 0) {
    //         aggregateScores.zip.state.negative += datapoint.zipAnalysis.stateImpacts.negative.score * weight;
    //         aggregateScores.zip.state.ids.add(datapoint.id);
    //     }
    //     if (datapoint.zipAnalysis.stateImpacts.positive.score > 0) {
    //         aggregateScores.zip.state.positive += datapoint.zipAnalysis.stateImpacts.positive.score * weight;
    //         aggregateScores.zip.state.ids.add(datapoint.id);
    //     }

    //     // FIPS scores
    //     if (datapoint.fipsAnalysis.countyImpacts.negative.score > 0) {
    //         aggregateScores.fips.county.negative += datapoint.fipsAnalysis.countyImpacts.negative.score * weight;
    //         aggregateScores.fips.county.ids.add(datapoint.id);
    //     }
    //     if (datapoint.fipsAnalysis.stateImpacts.negative.score > 0) {
    //         aggregateScores.fips.state.negative += datapoint.fipsAnalysis.stateImpacts.negative.score * weight;
    //         aggregateScores.fips.state.ids.add(datapoint.id);
    //     }
    //     if (datapoint.fipsAnalysis.stateImpacts.positive.score > 0) {
    //         aggregateScores.fips.state.positive += datapoint.fipsAnalysis.stateImpacts.positive.score * weight;
    //         aggregateScores.fips.state.ids.add(datapoint.id);
    //     }
    // });

    // Convert Sets to Arrays and normalize scores
    // Object.keys(aggregateScores).forEach(geoType => {
    //     Object.keys(aggregateScores[geoType]).forEach(disasterType => {
    //         const ids = Array.from(aggregateScores[geoType][disasterType].ids);
    //         if (ids.length > 0) {
    //             aggregateScores[geoType][disasterType].positive /= ids.length;
    //             aggregateScores[geoType][disasterType].negative /= ids.length;
    //         }
    //         aggregateScores[geoType][disasterType].ids = ids;
    //     });
    // });

    // const output = {
    //     zipAnalysis: {
    //         countyImpacts: {
    //             score: aggregateScores.zip.county.positive - aggregateScores.zip.county.negative,
    //             datapointIds: aggregateScores.zip.county.ids,
    //             disasterCount: countyDisasters.length
    //         },
    //         stateImpacts: {
    //             score: aggregateScores.zip.state.positive - aggregateScores.zip.state.negative,
    //             datapointIds: aggregateScores.zip.state.ids,
    //             disasterCount: stateDisasters.length
    //         }
    //     },
    //     fipsAnalysis: {
    //         countyImpacts: {
    //             score: aggregateScores.fips.county.positive - aggregateScores.fips.county.negative,
    //             datapointIds: aggregateScores.fips.county.ids,
    //             disasterCount: countyDisasters.length
    //         },
    //         stateImpacts: {
    //             score: aggregateScores.fips.state.positive - aggregateScores.fips.state.negative,
    //             datapointIds: aggregateScores.fips.state.ids,
    //             disasterCount: stateDisasters.length
    //         }
    //     },
    //     allDatapoints,
    //     rawData: {
    //         disasterCounts: {
    //             county: countyDisasters.length,
    //             state: stateDisasters.length
    //         }
    //     }
    // }

    const stateDisasterZipScore = aggregateScores.stateDisasters.zip.positive - aggregateScores.stateDisasters.zip.negative;
    const stateDisasterFipsScore = aggregateScores.stateDisasters.fips.positive - aggregateScores.stateDisasters.fips.negative;

    const countyDisasterZipScore = aggregateScores.countyDisasters.zip.positive - aggregateScores.countyDisasters.zip.negative;
    const countyDisasterFipsScore = aggregateScores.countyDisasters.fips.positive - aggregateScores.countyDisasters.fips.negative;

    const newOutput = {
        totalPossiblePoints: TOTAL_POSSIBLE_POINTS,
        stateDisasters: {
            count: stateDisasters.length,
            score: (stateDisasterZipScore * weightingZIP) + (stateDisasterFipsScore * weightingFIPS),
            zip: {
                score: stateDisasterZipScore,
                datapointIds: aggregateScores.stateDisasters.zip.ids,
            },
            fips: {
                score: stateDisasterFipsScore,
                datapointIds: aggregateScores.stateDisasters.fips.ids,
            }
        },
        countyDisasters: {
            count: countyDisasters.length,
            score: (countyDisasterZipScore * weightingZIP) + (countyDisasterFipsScore * weightingFIPS),
            zip: {
                score: countyDisasterZipScore,
                datapointIds: aggregateScores.countyDisasters.zip.ids,
            },
            fips: {
                score: countyDisasterFipsScore,
                datapointIds: aggregateScores.countyDisasters.fips.ids,
            }
        },
        allDatapoints,
    }

    const finalMarketScore = ProduceFinalMarketScore(newOutput);

    return finalMarketScore;
}